import axios from 'axios';
import { useEffect, useState, useCallback } from 'react';

import { useAppContext } from '../context/appContext';
import { useAppConfig } from '../context/AppConfigContext.jsx';
import { authenticateServer } from '../utils/authUtils';
import { useCognitoContext } from '../context/CognitoContext';
import { SET_BACKEND_STATUS_SUCCESS } from '../context/backendStatus/backendStatusActions';
import { useBackendStatusContext } from '../context/backendStatus/backendStatusContext';
import logger from '../utils/logger';
import logError from '../utils/logError';

// Only the main /readiness check enables global updates; other checks stay local.
const useHealthCheck = (url, signOut, { updateGlobalHealthStatus = false } = {}) => {
  const { api } = useAppConfig();
  const authBaseUrl = api.auth.baseUrl;
  const { setGlobalHealthStatus, isAcquireRunning } = useAppContext();
  const [healthStatus, setHealthStatus] = useState('checking');
  const [progress, setProgress] = useState(0);
  const { getAuthenticatedUser } = useCognitoContext();
  const backendStatus = useBackendStatusContext();

  const handleResponse = useCallback(
    (response) => {
      if (response.data.readiness === 'starting') {
        const { progress: progressValue } = response.data;
        setHealthStatus('starting');
        if (updateGlobalHealthStatus) setGlobalHealthStatus('starting');
        setProgress(progressValue);
      } else if (response.status === 200) {
        setHealthStatus('up');
        if (updateGlobalHealthStatus) setGlobalHealthStatus('up');
      } else {
        setHealthStatus('down');
        if (updateGlobalHealthStatus) setGlobalHealthStatus('down');
        setProgress(0);
      }

      if (response.status === 401 || response.status === 403) {
        signOut();
      }
    },
    [setGlobalHealthStatus, signOut, updateGlobalHealthStatus],
  );

  const refreshAuthentication = useCallback(async () => {
    if (!authBaseUrl) {
      return;
    }
    try {
      const user = await getAuthenticatedUser();
      if (user) {
        const session = await new Promise((resolve, reject) => {
          user.getSession((err, session) => {
            if (err) {
              reject(err);
            } else {
              resolve(session);
            }
          });
        });
        const idToken = session.getIdToken().getJwtToken();
        await authenticateServer(idToken, authBaseUrl);
      }
    } catch (authError) {
      logger.error(logError(authError, { action: 'reauthenticateServer' }));
      // TODO: Consider sign out user if re-authentication fails
    }
  }, [getAuthenticatedUser, authBaseUrl]);

  const handleError = useCallback(
    async (error) => {
      setHealthStatus('down');
      if (updateGlobalHealthStatus) setGlobalHealthStatus('down');
      setProgress(0);
      if (error.response) {
        const status = error.response.status;
        if (status === 401 || status === 403) {
          signOut();
        } else if (status === 500 || status === 502) {
          await refreshAuthentication();
        }
      }
    },
    [setGlobalHealthStatus, signOut, refreshAuthentication, updateGlobalHealthStatus],
  );

  const fetchStatus = useCallback(async () => {
    if (!url) {
      return;
    }
    try {
      const response = await axios.get(url);
      handleResponse(response);
    } catch (error) {
      handleError(error);
    }
  }, [url, handleResponse, handleError]);

  useEffect(() => {
    if (!isAcquireRunning && url) {
      const interval = setInterval(fetchStatus, 1000);
      return () => clearInterval(interval);
    }
  }, [fetchStatus, isAcquireRunning, url]);

  useEffect(() => {
    const isRebooting = localStorage.getItem('sonda.backend.rebooting') === 'true';
    if (isRebooting && healthStatus === 'starting') {
      localStorage.removeItem('sonda.backend.rebooting');
      backendStatus.dispatch({ type: SET_BACKEND_STATUS_SUCCESS });
    }
  }, [healthStatus]);

  return { healthStatus, progress };
};

export default useHealthCheck;
